import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import { useTheme, useMediaQuery } from "@material-ui/core";
import { Warning, Delete, CheckCircle, Info, Error } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "16px",
      boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
      overflow: "hidden",
      maxWidth: "420px",
      margin: "16px",
    },
  },
  header: {
    padding: "32px 32px 24px 32px",
    textAlign: "center",
    background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
    borderBottom: "1px solid #f0f0f0",
  },
  iconContainer: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
    transition: "all 0.3s ease",
  },
  warningIcon: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    "& svg": {
      fontSize: "32px",
    },
  },
  deleteIcon: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    "& svg": {
      fontSize: "32px",
    },
  },
  successIcon: {
    backgroundColor: "#d4edda",
    color: "#155724",
    "& svg": {
      fontSize: "32px",
    },
  },
  infoIcon: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
    "& svg": {
      fontSize: "32px",
    },
  },
  errorIcon: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    "& svg": {
      fontSize: "32px",
    },
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: "8px",
    lineHeight: 1.3,
  },
  content: {
    padding: "0 32px 32px 32px",
    textAlign: "center",
  },
  message: {
    fontSize: "14px",
    color: "#666",
    lineHeight: 1.5,
    marginBottom: "24px",
  },
  actions: {
    padding: "24px 32px 32px 32px",
    gap: "12px",
    justifyContent: "center",
  },
  cancelButton: {
    borderRadius: "8px",
    padding: "10px 24px",
    fontWeight: 600,
    textTransform: "none",
    border: "2px solid #e0e0e0",
    color: "#666",
    backgroundColor: "transparent",
    minWidth: "100px",
    "&:hover": {
      borderColor: "#1976d2",
      backgroundColor: "rgba(25, 118, 210, 0.04)",
      color: "#1976d2",
    },
  },
  confirmButton: {
    borderRadius: "8px",
    padding: "10px 24px",
    fontWeight: 600,
    textTransform: "none",
    minWidth: "100px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    },
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    color: "white",
    "&:hover": {
      backgroundColor: "#c82333",
    },
  },
  warningButton: {
    backgroundColor: "#ffc107",
    color: "#212529",
    "&:hover": {
      backgroundColor: "#e0a800",
    },
  },
  successButton: {
    backgroundColor: "#28a745",
    color: "white",
    "&:hover": {
      backgroundColor: "#218838",
    },
  },
  infoButton: {
    backgroundColor: "#17a2b8",
    color: "white",
    "&:hover": {
      backgroundColor: "#138496",
    },
  },
}));

const ModernConfirmationModal = ({ 
  title, 
  message, 
  open, 
  onClose, 
  onConfirm,
  type = "warning", // warning, delete, success, info, error
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  confirmColor = "primary"
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleConfirm = () => {
    onClose();
    onConfirm();
  };

  const handleCancel = () => {
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case "delete":
        return <Delete />;
      case "success":
        return <CheckCircle />;
      case "info":
        return <Info />;
      case "error":
        return <Error />;
      default:
        return <Warning />;
    }
  };

  const getIconClass = () => {
    switch (type) {
      case "delete":
        return classes.deleteIcon;
      case "success":
        return classes.successIcon;
      case "info":
        return classes.infoIcon;
      case "error":
        return classes.errorIcon;
      default:
        return classes.warningIcon;
    }
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case "delete":
        return `${classes.confirmButton} ${classes.deleteButton}`;
      case "warning":
        return `${classes.confirmButton} ${classes.warningButton}`;
      case "success":
        return `${classes.confirmButton} ${classes.successButton}`;
      case "info":
        return `${classes.confirmButton} ${classes.infoButton}`;
      default:
        return classes.confirmButton;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth={isMobile}
    >
      <div className={classes.header}>
        <div className={`${classes.iconContainer} ${getIconClass()}`}>
          {getIcon()}
        </div>
        <Typography className={classes.title}>
          {title}
        </Typography>
      </div>
      
      <DialogContent className={classes.content}>
        <Typography className={classes.message}>
          {message}
        </Typography>
      </DialogContent>
      
      <DialogActions className={classes.actions}>
        <Button
          onClick={handleCancel}
          className={classes.cancelButton}
          variant="outlined"
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          className={getConfirmButtonClass()}
          variant="contained"
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModernConfirmationModal;